import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { AppRoute, User } from '../../../utils/models';
import { getRelativeTime, summarize } from '../../../utils/utilities';
import { USER_TYPES, tutorialConstants } from '../../../utils/constants';
import { useAppDispatch, useAppSelector } from '../../../store/configureStore';

import { getConnectivity } from '../../../store/connectivity';
import { getTutorials, setActiveTutorial } from '../../../store/tutorials';

import Conditional from '../../common/conditional';
import Confirm from '../../themed/confirm';
import Elevation from '../../themed/instruction-modals/elevation';
import Modal from '../../themed/modal';

import * as authService from '../../../services/authService';
import * as offlineService from '../../../services/offlineService';
import * as toastService from '../../../services/toastService';

import * as actions from '../../../store/connectivity';

import css from './navigation.module.scss';
import routes from '../../../utils/routes';

import login from '../../../assets/images/login.svg';
import russelSmithLogo from '../../../assets/images/brand.png';

const Navigation: React.FC = () => {
	const [confirmLogout, setConfirmLogout] = useState(false);

	const { isSynchronizing, lastSynced } = useAppSelector(getConnectivity);
	const { activeTutorial } = useAppSelector(getTutorials);

	const dispatch = useAppDispatch();
	const location = useLocation();
	const navigate = useNavigate();

	const user = authService.getCurrentUser() as User;
	const queue: string[] = offlineService.getQueue();

	const getActiveClassName = (route: AppRoute): string => {
		let isActive: boolean = location.pathname === route.fullPath;

		let defaultClassName: string = css['navigation-list-item'];
		if (isActive) defaultClassName += ` ${css['active']}`;

		return defaultClassName;
	};

	const handleSyncRequests = (): void => {
		if (isSynchronizing)
			return toastService.error('Already synchronizing, please wait...');

		dispatch(actions.performSynchronization() as any);
	};

	const handleRestartGuide = () => {
		dispatch(setActiveTutorial(tutorialConstants.INTRODUCTION));

		navigate(
			user.userType === USER_TYPES.INTERNAL
				? routes.internalUserDashboard.fullPath
				: routes.externalUserDashboard.fullPath
		);
	};

	const navigateToLogout = () => {
		navigate(routes.logout.fullPath);
	};

	const renderRoute = (route: AppRoute, subtext?: string | number) => {
		const activeClassName = getActiveClassName(route);

		return (
			<li className={activeClassName}>
				<div className={css.icon}>
					<i className={'bi bi-' + route.icon}></i>
				</div>

				<span>
					<Link to={route.fullPath}>{route.name}</Link>

					<Conditional isVisible={!!subtext}>
						<div className={css['navigation-list-item-subtext']}>{subtext}</div>
					</Conditional>
				</span>

				<i className="bi bi-chevron-right"></i>
			</li>
		);
	};

	const toggleConfirmLogout = () => {
		setConfirmLogout((previous: boolean) => !previous);
	};

	return (
		<React.Fragment>
			<Modal isVisible={confirmLogout} style={{ height: 'auto' }} onClose={toggleConfirmLogout}>
				<div className="p-4">
					<div className={css['navigation-logout-image']}>
						<img src={login} alt="Logout" />
					</div>

					<div className="divider my-4"></div>

					<Confirm
						title="Confirm Logout"
						message="Are you sure you want to logout now? Make sure to cross your Ts and dot all your Is!"
						onCancel={toggleConfirmLogout}
						onConfirm={navigateToLogout}
					/>
				</div>
			</Modal>

			<div className={css.navigation}>
				<div className={css['navigation-topbar']}>
					<div className={css['navigation-topbar-logo']}>
						<img src={russelSmithLogo} alt="Russel Smith" />
					</div>
				</div>
				<div className={css.role}>
					<div className={css['role-label']}>Signed in as</div>
					<div className={css['role-value']}>
						{summarize(user.roleName as string, 10)}
					</div>
				</div>
				<ul className={css['navigation-list']}>
					<Conditional isVisible={user.userType === USER_TYPES.INTERNAL}>
						<Elevation isElevated={activeTutorial === tutorialConstants.INTERNAL_DASHBOARD}>
							{renderRoute(routes.internalUserDashboard)}
						</Elevation>
					</Conditional>

					<Conditional isVisible={user.userType === USER_TYPES.EXTERNAL}>
						<Elevation isElevated={activeTutorial === tutorialConstants.EXTERNAL_DASHBOARD}>
							{renderRoute(routes.externalUserDashboard)}
						</Elevation>
					</Conditional>

					<Conditional isVisible={user.userType === USER_TYPES.INTERNAL}>
						<Elevation isElevated={activeTutorial === tutorialConstants.ALL_PROJECTS}>
							{renderRoute(routes.projects)}
						</Elevation>
					</Conditional>

					<Conditional isVisible={user.role.viewReports || user.role.viewAssociatedReports}>
						<Elevation isElevated={activeTutorial === tutorialConstants.INSPECTION_REPORT}>
							{renderRoute(routes.organizationDashboard)}
						</Elevation>
					</Conditional>
					
					<Conditional isVisible={user.role.approveReports}>
						{renderRoute(routes.archivedProjects)}
					</Conditional>
					
					<Conditional isVisible={user.role.approveReports || user.role.viewAssociatedReports || user.role.viewReports}>
						{renderRoute(routes.inspectionTracker)}
					</Conditional>

					<Conditional isVisible={user.role.manageUsers}>
						<Elevation
							isElevated={[
								tutorialConstants.MANAGE_USERS,
								tutorialConstants.SETUP_USER,
								tutorialConstants.SETUP_DIVISIONS,
								tutorialConstants.SETUP_PRIVILEGES,
							].includes(activeTutorial as number)}
						>
							{renderRoute(routes.users)}
						</Elevation>
					</Conditional>

					<Conditional isVisible={user.role.manageRoles}>
						<Elevation isElevated={activeTutorial === tutorialConstants.MANAGE_ORGANIZATIONS}>
							{renderRoute(routes.organizations)}
						</Elevation>
					</Conditional>

					<Elevation isElevated={activeTutorial === tutorialConstants.PROFILE_SETTINGS}>
						{renderRoute(routes.profileSettings)}
					</Elevation>

					<Elevation isElevated={activeTutorial === tutorialConstants.VIEW_TUTORIALS}>
						{renderRoute(routes.tutorials)}
					</Elevation>

					<Conditional isVisible={activeTutorial === tutorialConstants.INACTIVE}>
						<button
							className={css['navigation-guide']}
							onClick={handleRestartGuide}
						>
							<i className="bi bi-signpost-2-fill"></i>
							<span>Restart Tutorial</span>
							<i className="bi bi-chevron-right"></i>
						</button>
					</Conditional>

					<button
						className={css['navigation-logout']}
						onClick={toggleConfirmLogout}
					>
						<i className={'bi bi-' + routes.logout.icon}></i>
						<span>{routes.logout.name}</span>
						<i className="bi bi-chevron-right"></i>
					</button>
				</ul>

				<Conditional isVisible={!!lastSynced}>
					<div className={css['navigation-sync-time']}>
						<div className={css['navigation-sync-time-item']}>
							<i className="bi bi-arrow-clockwise"></i>
							<span>Last Sync: </span>
							<div>{getRelativeTime(lastSynced as number)}</div>
						</div>
					</div>
				</Conditional>

				<button
					className={css['navigation-queued-item']}
					disabled={queue.length === 0}
					onClick={handleSyncRequests}
				>
					<i className="bi bi-cloud-arrow-up-fill"></i>
					<span>Queued for Sync</span>
					<div className={css['navigation-list-item-subtext']}>
						{queue.length}
					</div>
				</button>
			</div>
		</React.Fragment>
	);
};

export default Navigation;
